// 個人設定: 自分の情報とパスワード再設定（portal.md「画面と操作（契約単位）」）。ヘッダのメニューから開く。
// パスワード再設定は画面の中で完結させる（lib/password-reset.ts）。

import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { emailDomainOf } from "../lib/login";
import { confirmReset, resetMessageFor, sendResetCode } from "../lib/password-reset";
import { useContract } from "./ContractLayout";

type Method = "sso" | "password" | "unknown";

export function ProfilePage() {
  const { me, membership } = useContract();
  const [method, setMethod] = useState<Method | null>(null);

  useEffect(() => {
    // ログイン方式は共通ログイン画面と同じく email のドメインで決まる。引けなければ不明とし、
    // パスワード再設定は出さない（可否の判定はサーバー側の Custom Message トリガーが行う）。
    const domain = emailDomainOf(me.email);
    if (!domain) { setMethod("unknown"); return; }
    api.idpLookup(domain)
      .then((r) => setMethod(r.idpName ? "sso" : "password"))
      .catch(() => setMethod("unknown"));
  }, [me.email]);

  return (
    <main>
      <h1>個人設定</h1>
      <h2>自分の情報</h2>
      <table>
        <tbody>
          <tr><th>メールアドレス</th><td>{me.email}</td></tr>
          <tr><th>ログイン方式</th><td>{method === null ? "…" : method === "sso" ? "組織管理対象" : method === "password" ? "メール/パスワード" : "確認できませんでした"}</td></tr>
          <tr><th>所属チーム</th><td>{membership?.teams.map((t) => t.name).join("、") || "—"}</td></tr>
          <tr><th>権限</th><td>{me.platformRole === "system-admin" ? "システム管理者" : membership?.isAdmin ? "管理者" : "メンバー"}</td></tr>
        </tbody>
      </table>
      <h2>パスワード</h2>
      {method === "sso" && <p className="muted">組織の SSO でログインしているため、パスワードは組織の IdP で管理されます。</p>}
      {method === "password" && <PasswordReset email={me.email} />}
      {method === "unknown" && <p className="muted">ログイン方式を確認できなかったため、パスワードの再設定を表示できません。時間をおいて開き直してください。</p>}
    </main>
  );
}

/** パスワード再設定: 確認コードを email に送り、コードと新しいパスワードで確定する。 */
function PasswordReset({ email }: { email: string }) {
  const [step, setStep] = useState<"idle" | "sent" | "done">("idle");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const run = async (f: () => Promise<void>) => {
    setBusy(true); setError(null);
    try { await f(); } catch (e) { setError(resetMessageFor(e)); } finally { setBusy(false); }
  };

  const send = () => run(async () => { await sendResetCode(email); setStep("sent"); });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== confirm) { setError("新しいパスワードが一致しません。"); return; }
    void run(async () => {
      await confirmReset(email, code.trim(), password);
      setStep("done"); setCode(""); setPassword(""); setConfirm("");
    });
  };

  if (step === "done") return <p>パスワードを再設定しました。次回のログインから新しいパスワードを使ってください。</p>;

  return (
    <>
      {step === "idle" && (
        <p>
          <button type="button" onClick={() => void send()} disabled={busy}>{busy ? "送信中…" : "パスワードを再設定する"}</button>
          <span className="muted">　{email} に確認コードを送ります。</span>
        </p>
      )}
      {step === "sent" && (
        <form onSubmit={submit}>
          <p className="muted">{email} に確認コードを送りました。届かない場合は迷惑メールフォルダも確認してください。</p>
          <label htmlFor="reset-code">確認コード</label>
          <input id="reset-code" type="text" value={code} onChange={(e) => setCode(e.target.value)} inputMode="numeric" autoComplete="one-time-code" required />
          <label htmlFor="reset-password">新しいパスワード</label>
          <input id="reset-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" required />
          <label htmlFor="reset-confirm">新しいパスワード（確認）</label>
          <input id="reset-confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" required />
          <p className="muted">8 文字以上で、英大文字・英小文字・数字を含めてください。</p>
          <div className="row">
            <button className="primary" type="submit" disabled={busy}>{busy ? "変更中…" : "パスワードを変更"}</button>
            <button type="button" onClick={() => void send()} disabled={busy}>コードを再送</button>
          </div>
        </form>
      )}
      {error && <p className="error">{error}</p>}
    </>
  );
}
